/**
 * The shared clock and the age vocabulary behind every "N ago" the tracker
 * feature shows: the badge on the datasets list, the "last applied" line on
 * a team card. One module, so the thresholds that turn an age green, amber
 * or red are the same wherever an age is shown — and a non-component module,
 * so the components that import it keep Fast Refresh.
 */

import { useSyncExternalStore } from 'react';

const clockListeners = new Set<() => void>();
let clockNow = Date.now();
let clockTimer: ReturnType<typeof setInterval> | null = null;

function subscribeClock(listener: () => void): () => void {
  clockListeners.add(listener);
  if (clockTimer === null) {
    clockTimer = setInterval(() => {
      clockNow = Date.now();
      clockListeners.forEach((l) => l());
    }, 1000);
  }
  return () => {
    clockListeners.delete(listener);
    if (clockListeners.size === 0 && clockTimer !== null) {
      clearInterval(clockTimer);
      clockTimer = null;
    }
  };
}

export function useNow(): number {
  return useSyncExternalStore(
    subscribeClock,
    () => clockNow,
    () => clockNow,
  );
}

/**
 * How old this dataset's data is — driven by the last SUCCESSFUL sync, never
 * by the latest attempt.
 *
 * The badge previously keyed on `latest.status !== 'ok'`, so one failed
 * refresh relabelled a dataset that had synced many times as `never synced`,
 * beside the `last sync failed` badge that contradicts it. Worse than
 * cosmetic: this badge is the only signal of data age, so after any failure an
 * operator could not tell "no data at all" from "good data plus a failed
 * refresh" — exactly the judgement needed while debugging a bad sync
 * (shipcast-78m.4).
 *
 * Takes the whole sync state rather than a bare timestamp so the component
 * holds both facts and the choice between them stays visible here, where the
 * mistake was made. `latest` drives the failure and progress badges in the
 * row; it must never drive the age.
 */
/**
 * The three ages an operator reads differently: fresh, a day old, older.
 * One place for the thresholds, so the badge on the datasets list and the
 * "last applied" line on a team card cannot disagree about which is which.
 */
export type StalenessTone = 'success' | 'warning' | 'error';

export function stalenessTone(ageMs: number): StalenessTone {
  const ageH = ageMs / (1000 * 60 * 60);
  if (ageH < 1) return 'success';
  if (ageH < 24) return 'warning';
  return 'error';
}

export function formatAge(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
